import React, { ReactNode, useState, useEffect } from 'react';
import { Card } from '@material-tailwind/react';
import { AutoSizer, List, ListRowRenderer } from 'react-virtualized';
import { ReactComponent as ArrowDownIcon } from '@assets/icons/arrow_down_icon.svg';
import clsx from 'clsx';
import _get from 'lodash/get';
import 'react-virtualized/styles.css';

export type TableHeader = {
  label: string;
  field: string;
  render?: (row: any) => ReactNode;
  headerClassName?: string;
  contentClassName?: string;
  sort?: boolean;
  onSort?: (field: string) => void;
};

type Props = {
  headers: TableHeader[];
  data: Record<string, any>[];
  onClickRow?: (rowData: any) => void;
  height?: number;
  itemHeight?: number;
  sortKey?: string | null;
  sortDirection?: 'asc' | 'desc';
};

const VirtualTable: React.FC<Props> = ({
  headers = [],
  data = [],
  onClickRow,
  height, // height prop is now optional since we calculate dynamically
  itemHeight = 64,
  sortKey,
  sortDirection
}) => {
  // Dynamic height calculation
  const [dynamicHeight, setDynamicHeight] = useState(600);
  
  useEffect(() => {
    const calculateHeight = () => {
      const screenHeight = window.innerHeight;
      const calculatedHeight = screenHeight - 450;
      setDynamicHeight(Math.max(calculatedHeight, 300)); // Minimum height of 300px
    };

    // Calculate on mount
    calculateHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  // Use provided height or dynamic height
  const tableHeight = height || dynamicHeight;

  // Safety checks
  if (!Array.isArray(headers) || !Array.isArray(data)) {
    return (
      <Card className="h-full w-full overflow-hidden border border-grey-200 shadow-none">
        <div className="flex items-center justify-center py-12 text-grey-500">
          Invalid data provided
        </div>
      </Card>
    );
  }

  const rowRenderer: ListRowRenderer = ({ index, key, style }) => {
    const row = data[index];

    if (!row) {
      return (
        <div key={key} style={style} className="border-b border-grey-200">
          <div className="flex items-center justify-center min-h-[64px] text-grey-500">
            No data
          </div>
        </div>
      );
    }

    return (
      <div key={key} style={style} className="border-b border-grey-200">
        <div
          className="flex cursor-pointer hover:bg-grey-50 min-h-[64px] items-center"
          onClick={onClickRow ? () => onClickRow(row) : undefined}>
          {headers.map((head: TableHeader) => (
            <div
              key={head.field}
              className={clsx(
                'px-6 py-5 flex-1 min-w-0',
                head.contentClassName
              )}
              style={{ 
                flex: head.field === 'action' ? '0 0 120px' : '1',
                minWidth: head.field === 'action' ? '120px' : '150px'
              }}>
              {head.render ? (
                head.render(row)
              ) : (
                <div className="text-sm truncate">{_get(row, head.field)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="h-full w-full overflow-hidden border border-grey-200 shadow-none">
      {/* Header */}
      <div className="border-b border-grey-200 bg-blue-gray-50/50">
        <div className="flex min-h-[48px] items-center">
          {headers.map((head) => (
            <div
              key={head.field}
              className={clsx(
                'px-6 py-3 flex-1 min-w-0',
                head.onSort && 'hover:bg-blue-gray-50 cursor-pointer',
                head.contentClassName
              )}
              style={{ 
                flex: head.field === 'action' ? '0 0 120px' : '1',
                minWidth: head.field === 'action' ? '120px' : '150px'
              }}
              onClick={head.onSort ? () => head.onSort!(head.field) : undefined}>
              <div className="flex items-center gap-2 text-xs text-grey-600 font-medium">
                {head.label}
                {head.onSort && (
                  <ArrowDownIcon
                    strokeWidth={2}
                    className={clsx(
                      'h-4 w-4 transition-transform duration-200',
                      sortKey === head.field
                        ? sortDirection === 'desc'
                          ? 'rotate-180'
                          : ''
                        : 'opacity-0'
                    )}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Virtual Scrolling Body */}
      <div style={{ height: tableHeight }}>
        {data.length > 0 ? (
          <AutoSizer>
            {({ height: autoHeight, width }) => (
              <List
                height={autoHeight}
                width={width}
                rowCount={data.length}
                rowHeight={itemHeight}
                rowRenderer={rowRenderer}
                className="virtual-table-list"
              />
            )}
          </AutoSizer>
        ) : (
          <div className="flex items-center justify-center py-12 text-grey-500">
            No data available
          </div>
        )}
      </div>
    </Card>
  );
};

export default VirtualTable;